import React from 'react';
import { Link } from 'react-router-dom';
import { Database, Zap, Clock, Star, Phone, Megaphone, MessageSquare, BarChart, ArrowRight, CheckCircle } from 'lucide-react';

const Solutions = () => {
  const solutions = [
    {
      icon: Database,
      title: 'Database Reactivation',
      description: 'Pull fresh sales from leads you\'ve already paid for and haven\'t bought, using conversational AI',
      benefits: [
        'Automatically engage dormant leads via SMS',
        'Conversational AI that feels natural',
        'Turn cold leads into hot prospects',
        'Generate £10K-£100K+ in additional sales'
      ],
      gradient: 'from-blue-500 to-cyan-500',
      stats: { value: '£100K+', label: 'Revenue Recovered' }
    },
    {
      icon: Zap,
      title: 'Speed To Lead',
      description: 'Stop losing sales to competitors who are faster to the phone, by getting AI to work your leads 24/7',
      benefits: [
        'Instant response to new leads',
        'Never miss a hot prospect again',
        'Qualify leads automatically',
        'Beat competitors to the punch'
      ],
      gradient: 'from-purple-500 to-pink-500',
      stats: { value: '<60s', label: 'Response Time' }
    },
    {
      icon: Clock,
      title: 'Out Of Hours',
      description: 'Stop \'Out of Hours\' leads going unanswered and wasting time each morning on unqualified prospects with AI',
      benefits: [
        '24/7 lead engagement',
        'Automatic qualification',
        'No more morning backlog',
        'Capture weekend & evening leads'
      ],
      gradient: 'from-orange-500 to-red-500',
      stats: { value: '24/7', label: 'Always Active' }
    },
    {
      icon: Star,
      title: 'Google Reviews',
      description: 'Improve business credibility and customer opinion replying to reviews without spending man-hours using AI',
      benefits: [
        'Automated review responses',
        'Maintain brand voice',
        'Improve online reputation',
        'Save hours of manual work'
      ],
      gradient: 'from-green-500 to-emerald-500',
      stats: { value: '5★', label: 'Rating Boost' }
    },
    {
      icon: Phone,
      title: 'Voice AI',
      description: 'Use AI to handle inbound calls to redirect leads, take information and book / rearrange appointments',
      benefits: [
        'Natural voice conversations',
        'Appointment booking & management',
        'Call routing & information gathering',
        'Reduce receptionist workload'
      ],
      gradient: 'from-indigo-500 to-blue-500',
      stats: { value: '1000+', label: 'Calls Handled' }
    },
    {
      icon: Megaphone,
      title: 'AI Advertisements',
      description: 'Professional Business Advertisements without the extra production, cost or time made to look human-like using AI',
      benefits: [
        'Professional quality ads without high production costs',
        'Human-like AI-generated content',
        'Quick turnaround time',
        'Cost-effective marketing solutions'
      ],
      gradient: 'from-pink-500 to-rose-500',
      stats: { value: '10x', label: 'Cost Savings' }
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Discovery Call',
      description: 'We learn about your business, challenges, and goals to identify the best AI solutions for your needs.'
    },
    {
      number: '02',
      title: 'Custom Strategy',
      description: 'Our team designs a tailored AI implementation plan that aligns with your business objectives.'
    },
    {
      number: '03',
      title: 'Implementation',
      description: 'We deploy and integrate AI solutions seamlessly into your existing workflows and systems.'
    },
    {
      number: '04',
      title: 'Optimization',
      description: 'Continuous monitoring and refinement to ensure maximum ROI and performance.'
    }
  ];

  return (
    <div className="pt-16">
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary-600 via-accent-600 to-primary-700 text-white">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
            <span className="text-white font-semibold text-sm">AI-Powered Solutions</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 leading-tight">
            Transform Your Business
            <br />
            <span className="text-primary-200">With AI Solutions</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto leading-relaxed font-medium">
            Comprehensive AI services designed to streamline operations, boost sales, and maximize productivity
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              <span className="text-gradient">Our Solutions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Cutting-edge AI technology tailored to solve your specific business challenges
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover-lift border border-gray-100"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${solution.gradient} group-hover:scale-110 transition-transform`}>
                    <solution.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-right">
                    <div className={`text-3xl font-bold bg-gradient-to-r ${solution.gradient} bg-clip-text text-transparent`}>
                      {solution.stats.value}
                    </div>
                    <div className="text-sm text-gray-600">{solution.stats.label}</div>
                  </div>
                </div>

                <h3 className="text-2xl font-display font-bold mb-4 text-gray-900">
                  {solution.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {solution.description}
                </p>

                <div className="space-y-3">
                  {solution.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              <span className="text-gradient">How It Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our proven process for implementing AI solutions that deliver results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                  <div className="text-6xl font-display font-bold text-gradient mb-4 opacity-20">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-display font-bold mb-3 text-gray-900">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-primary-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-primary-600 to-accent-600 rounded-3xl overflow-hidden shadow-2xl">
            <div className="grid md:grid-cols-2">
              <div className="p-12 flex flex-col justify-center text-white">
                <MessageSquare className="w-16 h-16 mb-6" />
                <h2 className="text-4xl font-display font-bold mb-6">
                  Make More Sales & Get Better Qualified Leads
                </h2>
                <p className="text-xl text-primary-100 mb-6 leading-relaxed">
                  Find out if you can remove expensive bottlenecks and add a few extra zeros to your business account with AI.
                </p>
                <p className="text-lg text-primary-100 mb-8">
                  Book in a call here, so we can chat.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center bg-white text-primary-600 px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 w-fit"
                >
                  Schedule a Call
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
              <div className="relative bg-gradient-to-br from-primary-700 to-accent-700 p-12 flex items-center justify-center">
                <div className="text-center">
                  <BarChart className="w-32 h-32 text-white/20 mx-auto mb-8" />
                  <div className="space-y-6">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                      <div className="text-4xl font-bold text-white mb-2">£10K - £100K+</div>
                      <div className="text-primary-100">Additional Sales Generated</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                      <div className="text-4xl font-bold text-white mb-2">500+</div>
                      <div className="text-primary-100">Leads Reactivated</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-white mb-10 leading-relaxed font-medium">
            Join hundreds of businesses already leveraging AI to unlock unprecedented growth and efficiency.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-gradient-to-r from-primary-600 to-accent-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-gray-900 transition-all duration-300"
            >
              Learn More About Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
